import type { TFunction } from "i18next";
import type { PageSearchEntry } from "~/helpers/search/pageSearchIndex";
import type { KeyboardActionId } from "~/keyboard/actionCatalog";

import rankByLabel from "~/helpers/search/labelMatcher";
import { pageSearchLabel } from "~/helpers/search/pageSearchIndex";

export type CommandGroup = "action" | "navigation";

export interface CommandDefinition {
	category: string;
	color?: string;
	group: CommandGroup;
	icon: string;
	id: string;
	keywords?: string[];
	label: string;
	run: () => void;
	shortcutActionId?: KeyboardActionId;
}

const commandSearchText = (command: CommandDefinition): string =>
	[command.label, command.category, ...(command.keywords ?? [])].join(" ");

const buildNavigationCommands = (
	pages: PageSearchEntry[],
	navigate: (path: string) => void,
	t: TFunction<"common">,
	excludePaths: ReadonlySet<string> = new Set()
): CommandDefinition[] =>
	pages
		.filter((page) => !excludePaths.has(page.path))
		.map((page) => ({
			category: t("commands.categories.navigation"),
			color: page.color,
			group: "navigation",
			icon: page.icon,
			id: `nav-${page.path}`,
			keywords: [page.module],
			label: t("commands.gotoPage", { page: pageSearchLabel(page, t) }),
			run: () => navigate(page.path)
		}));

const filterCommands = (commands: CommandDefinition[], query: string, limit: number): CommandDefinition[] => {
	const trimmed = query.trim();
	if (!trimmed) {
		return commands.filter((command) => command.group === "action");
	}
	return rankByLabel(commands, commandSearchText, trimmed, { limit }).map((match) => match.item);
};

export { buildNavigationCommands, commandSearchText };
export default filterCommands;
